/**
 * AiExplainDialog
 *
 * A focused dialog for the "Explain with AI" context menu action.
 * Auto-fires an explanation of the selected code on open, then lets the
 * user ask follow-up questions in a simple chat thread.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sparkles, Settings, Send, Square, Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { OpenRouterModel } from '@/types/ai';
import { useAiSettings } from '@/hooks/useAiSettings';
import { useAiExplain } from '@/hooks/useAiExplain';
import { createAiClient, fetchModels } from '@/services/ai';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AiTokenDisplay } from './AiTokenDisplay';
import { AiSettingsDialog } from './AiSettingsDialog';
import { ModelSelector } from './ModelSelector';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AiExplainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The code the user selected in the editor */
  selectedCode: string;
  /** The full editor content — sent as background context */
  contextCode: string;
}

export function AiExplainDialog({
  open,
  onOpenChange,
  selectedCode,
  contextCode,
}: AiExplainDialogProps) {
  const {
    settings,
    isConfigured,
    setApiKey,
    setDefaultModel,
    setPromptTemplate,
    resetPromptTemplates,
  } = useAiSettings();

  const [selectedModel, setSelectedModel] = useState(settings.defaultModel);
  const [showSettings, setShowSettings] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [models, setModels] = useState<OpenRouterModel[]>([]);

  const selectedModelInfo = useMemo(
    () => models.find((m) => m.id === selectedModel) ?? null,
    [models, selectedModel],
  );

  const {
    state,
    isExplaining,
    tokenUsage,
    explain,
    sendFollowUp,
    cancel,
    reset,
  } = useAiExplain();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync default model from settings
  useEffect(() => {
    if (settings.defaultModel && !selectedModel) {
      setSelectedModel(settings.defaultModel);
    }
  }, [settings.defaultModel, selectedModel]);

  // Load models when dialog opens
  useEffect(() => {
    if (open && isConfigured) {
      const loadModels = async () => {
        try {
          const client = createAiClient(settings.apiKey);
          const fetched = await fetchModels(client);
          setModels(fetched);
        } catch {
          // Non-critical — model selector still works with a stale list
        }
      };
      loadModels();
    }
  }, [open, isConfigured, settings.apiKey]);

  // Auto-trigger the initial explanation when the dialog opens
  useEffect(() => {
    if (open && isConfigured && selectedCode && selectedModel) {
      explain(
        settings.apiKey,
        selectedCode,
        contextCode,
        selectedModel,
        selectedModelInfo,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // intentionally only on open toggle

  // Reset when the dialog closes
  useEffect(() => {
    if (!open) {
      reset();
      setFollowUp('');
    }
  }, [open, reset]);

  // Scroll to the bottom whenever the message list or streaming content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.currentContent]);

  const handleSendFollowUp = useCallback(async () => {
    const q = followUp.trim();
    if (!q || isExplaining) return;
    setFollowUp('');
    await sendFollowUp(settings.apiKey, q, selectedModel, selectedModelInfo);
  }, [
    followUp,
    isExplaining,
    sendFollowUp,
    settings.apiKey,
    selectedModel,
    selectedModelInfo,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSendFollowUp();
      }
    },
    [handleSendFollowUp],
  );

  const hasStarted = state.messages.length > 0 || isExplaining;
  const canSendFollowUp =
    !isExplaining && state.step === 'complete' && followUp.trim().length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!flex h-[85vh] max-h-[85vh] flex-col !gap-0 sm:max-w-[660px]">
          {/* Header */}
          <DialogHeader className="flex-shrink-0 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <DialogTitle>Explain with AI</DialogTitle>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSettings(true)}
                    className="h-8 w-8"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="sr-only">AI Settings</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>AI Settings</TooltipContent>
              </Tooltip>
            </div>
          </DialogHeader>

          {/* Model selector — hidden once conversation starts */}
          {!hasStarted && (
            <div className="flex-shrink-0 space-y-3 pb-3">
              {!isConfigured && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please configure your OpenRouter API key in{' '}
                    <button
                      onClick={() => setShowSettings(true)}
                      className="font-medium underline underline-offset-2"
                    >
                      settings
                    </button>{' '}
                    to use AI features.
                  </AlertDescription>
                </Alert>
              )}
              <ModelSelector
                apiKey={settings.apiKey}
                value={selectedModel}
                onValueChange={(value) => {
                  setSelectedModel(value);
                  setDefaultModel(value);
                }}
                disabled={!isConfigured || isExplaining}
              />
            </div>
          )}

          {/* Selected code preview — shown before explanation starts */}
          {!hasStarted && selectedCode && (
            <div className="mb-3 flex-shrink-0 rounded-md border border-border bg-muted/40 px-3 py-2">
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                Selected code
              </p>
              <pre className="max-h-[120px] overflow-auto whitespace-pre-wrap font-mono text-xs">
                {selectedCode}
              </pre>
            </div>
          )}

          {/* Chat thread */}
          <ScrollArea className="min-h-0 flex-1 rounded-md border border-border bg-background">
            <div className="flex flex-col gap-4 p-4">
              {state.messages.map((msg, i) => (
                <div
                  key={`${msg.role}-${i}`}
                  className={cn(
                    'flex gap-3',
                    msg.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {msg.role === 'assistant' && (
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Bot className="h-4 w-4" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted',
                    )}
                  >
                    {msg.role === 'user' ? (
                      /* User messages: show the raw question (not the full code prompt for first msg) */
                      <p className="whitespace-pre-wrap">
                        {i === 0 ? 'Explain the selected code' : msg.content}
                      </p>
                    ) : (
                      <div className="prose prose-sm max-w-none dark:prose-invert prose-code:before:content-none prose-code:after:content-none prose-pre:bg-background/60 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}

              {/* Streaming assistant response */}
              {isExplaining && state.currentContent && (
                <div className="flex justify-start gap-3">
                  <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm">
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-code:before:content-none prose-code:after:content-none prose-pre:bg-background/60 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {state.currentContent}
                      </ReactMarkdown>
                    </div>
                    <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-foreground/60 align-text-bottom" />
                  </div>
                </div>
              )}

              {/* Placeholder while waiting for first chunk */}
              {isExplaining && !state.currentContent && (
                <div className="flex justify-start gap-3">
                  <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="rounded-lg bg-muted px-3 py-2">
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
                    </span>
                  </div>
                </div>
              )}

              {/* Error */}
              {state.step === 'error' && state.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{state.error}</AlertDescription>
                </Alert>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Token usage */}
          {tokenUsage.totalTokens > 0 && (
            <div className="flex-shrink-0 pt-2">
              <AiTokenDisplay tokenUsage={tokenUsage} />
            </div>
          )}

          {/* Follow-up input */}
          {hasStarted && (
            <div className="flex-shrink-0 pt-3">
              <div className="flex gap-2">
                <Textarea
                  ref={textareaRef}
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a follow-up question… (Cmd/Ctrl+Enter to send)"
                  rows={2}
                  disabled={isExplaining}
                  className="resize-none"
                />
                {isExplaining ? (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-auto self-stretch"
                    onClick={cancel}
                    title="Cancel"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="h-auto self-stretch"
                    onClick={handleSendFollowUp}
                    disabled={!canSendFollowUp}
                    title="Send (Cmd+Enter)"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Footer close button — only when conversation hasn't started */}
          {!hasStarted && (
            <div className="mt-2 flex flex-shrink-0 justify-end border-t pt-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AiSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        apiKey={settings.apiKey}
        onApiKeyChange={setApiKey}
        promptTemplates={settings.promptTemplates}
        onPromptTemplateChange={setPromptTemplate}
        onResetPromptTemplates={resetPromptTemplates}
      />
    </>
  );
}
